<template>
  <div class="modal-overflow">
    <div class="row">
      <div class="col-md-9">
        <label>Job Template Name</label>
        <el-input
          name="name"
          data-vv-as="job template name"
          v-model="edit.name"
          v-validate="modelValidations.jobTemplateName"
          :placeholder="jobTemplateNamePlaceholder()" />
        <small class="text-danger" v-show="errors.has('name')">
          {{ getError('name') }}
        </small>
      </div>
      <div class="col-md-3">
        <label>Department</label>
        <el-select class="select"
                   size="large"
                   placeholder="Departments"
                   v-model="edit.departmentId">
          <el-option v-for="option in allDepartments"
                     class="select"
                     :value="option.value"
                     :label="option.label"
                     :key="option.label">
          </el-option>
        </el-select>
      </div>
    </div>
    <div class="row">
      <div class="col-md-6">
        <label>Job Name</label>
        <div style="padding-top: 9px;font-family: Montserrat,Helvetica Neue,Arial,sans-serif;">{{ edit.jobName }}</div>
      </div>
      <div class="col-md-3">
        <label>Priority</label>
        <el-select class="select width-100"
                   size="large"
                   placeholder="Priority"
                   clearable
                   v-model="edit.priority">
          <el-option v-for="option in priorities"
                     class="select"
                     :value="option.value"
                     :label="option.label"
                     :key="option.label">
          </el-option>
        </el-select>
      </div>
      <div class="col-md-3">
        <label>Community</label>
        <el-autocomplete
          style="width:100%"
          ref="communityId"
          name="community"
          class="inline-input"
          clearable
          :debounce="150"
          v-model="selectedCommunity.name"
          :fetch-suggestions="findCommunities"
          :trigger-on-focus="false"
          value-key="name"
          placeholder="Community"
          @focus="verifyClientOrBuilderSelected"
          @blur="verifyCommunityAutocompleteSelected"
          @select="setCommunityWithExistsCheck">
        </el-autocomplete>
        <small class="text-danger" v-show="showInvalidAutocompleteWarning('communityId')">
          {{ fieldNotValidError('Community') }}
        </small>
      </div>
    </div>
    <div class="row">
      <div class="col-md-3">
        <label>Billable Client</label>
        <el-autocomplete
          style="width:100%"
          name="clientId"
          ref="clientId"
          class="inline-input"
          :debounce="100"
          clearable
          v-model="selectedClient.name"
          :fetch-suggestions="findClients"
          :trigger-on-focus="false"
          value-key="name"
          placeholder="Billable Client"
          @blur="verifyBillableClientAutocompleteSelected"
          @select="setClient">
        </el-autocomplete>
        <small class="text-danger" v-show="showInvalidAutocompleteWarning('clientId')">
          {{ fieldNotValidError('Client') }}
        </small>
      </div>
      <div class="col-md-3">
        <label>Client Contact</label>
        <el-select class="select"
                   name="clientContactId"
                   size="large"
                   clearable
                   placeholder="Client Contact"
                   v-model="edit.clientContactId">
          <el-option v-for="option in clientContacts"
                     class="select"
                     :value="option.value"
                     :label="option.label"
                     :key="option.value">
          </el-option>
        </el-select>
      </div>
      <div class="col-md-3">
        <label>Builder</label>
        <el-autocomplete
          style="width:100%"
          name="builderId"
          ref="builderId"
          class="inline-input"
          :debounce="150"
          :clearable="true"
          v-model="selectedBuilder.name"
          :fetch-suggestions="findBuilders"
          :trigger-on-focus="false"
          value-key="name"
          placeholder="Builder"
          @blur="verifyBuilderAutocompleteSelected"
          @select="setBuilder">
        </el-autocomplete>
        <small class="text-danger" v-show="showInvalidAutocompleteWarning('builderId')">
          {{ fieldNotValidError('Builder') }}
        </small>
      </div>
      <div class="col-md-3">
        <label>Builder Contact</label>
        <el-select class="select"
                   name="builderContactId"
                   size="large"
                   clearable
                   placeholder="Builder Contact"
                   v-model="edit.builderContactId">
          <el-option v-for="option in builderContacts"
                     class="select"
                     :value="option.value"
                     :label="option.label"
                     :key="option.value">
          </el-option>
        </el-select>
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <table class="bulk-tasks" cellspacing="0" cellpadding="0" border="0" style="width: 100%">
          <colgroup>
            <col style="width:45%">
            <col style="width:35%">
            <col style="width:20%">
          </colgroup>
          <tr>
            <th>Task Name</th>
            <th>Assignee</th>
            <th>Due Date (Out of Service)</th>
          </tr>
          <tr v-for="(task, index) in edit.tasks">
            <td>{{ task.name }}</td>
            <td style="padding: 3px 5px;">
              <v-select
                :value="task.assigneeName"
                placeholder="Assignee"
                @input="assigneeOption => setAssignee(task, assigneeOption, index)"
                label="label"
                code="value"
                :options="users"
                @focus="setActiveAssigneeAutocomplete(task.nameId)">
              </v-select>
            </td>
            <td style="padding: 3px 5px;">
              <v-select
                :value="task.dueDatePlusDays"
                placeholder="Out of Service"
                label="day"
                code="day"
                :options="numberOfDays">
              </v-select>
            </td>
          </tr>
        </table>
      </div>
    </div>
    <div class="row">
       <div class="col-md-6">
        <auditable-entity v-if="data && data.id" :data="edit"></auditable-entity>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <button :disabled="isSubmitDisabled" @click="validateAndSubmit" class="btn btn-fill btn-info btn-wd btn-save pull-right">Save Job Template</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import {sync} from 'vuex-pathify'
import {
  findCommunities,
  contactsForSelect,
  notifyError,
  notifySuccess,
  notifyWarning,
  verifyAutocompleteSelected,
  objectBy,
  objectNameById,
  autocompleteTextButNoValueMessage,
  autocompleteTextButNoValue
} from 'src/utilities/utils'
import Constants from 'src/utilities/constants'
import {OK} from 'http-status-codes'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import _ from 'lodash'
import AuditableEntity from 'src/components/UIComponents/AuditableEntity'
import {communityExistsButNotAssociated} from '@/utilities/jobHelper'


export default{
    props: ['data'],
    components: {
      vSelect,
      AuditableEntity,
    },
    computed: {
      edit: sync('jobTemplates@edit'),
      ...mapGetters({
        departments: 'departments/results',
        allDepartments: 'departments/kvpsForSelect',
        activeModals: 'modalTracker/activeModals',
        autocompleteClients: 'companies/autocompleteResultsClient',
        autocompleteBuilders: 'companies/autocompleteResultsBuilder',
        priorities: 'priorities/job',
        users: 'users/kvpsForSelect'
      }),
      tableData() {
        return this.edit.tasks
      },
      isSubmitDisabled() {
        return this.isProcessing
      }
    },
    data() {
      return {
        numberOfDays: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
        selectedPlanName: null,
        selectedCoordinator: null,
        selectedCommunity: { id: null, name: null },
        selectedClient: { id: null, name: null },
        selectedBuilder: { id: null, name: null },
        clientContacts: [],
        builderContacts: [],
        selectedAssigneeInput: null,
        isProcessing: false,
        modelValidations: {
          jobTemplateName: {
            required: true,
            max: 128
          }
        }
      }
    },
    methods: {
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      showInvalidAutocompleteWarning(refAndPathName) {
        // $ref has to match prop in store
        return autocompleteTextButNoValue(this, refAndPathName, this.$store.get(`jobTemplates@edit.${refAndPathName}`))
      },
      fieldNotValidError(field) {
        return autocompleteTextButNoValueMessage(field)
      },
      jobTemplateNamePlaceholder() {
        if (this.data && this.data.job) {
          return this.data.job.name
        }
        return ''
      },
      departmentName() {
        return objectNameById(this.departments, this.$store.get('jobTemplates@edit.departmentId'))
      },
      async validateAndSubmit() {
        this.isProcessing = true

        if (await this.$validator.validateAll()) {
          const payload = this.$store.get('jobTemplates/savePayload', 'job-templates')
          const response = await this.$store.dispatch('save', payload)
          if (response.result === Constants.RETURN_CODES.SUCCESS) {
            notifySuccess(this, 'Saved Job Template Successfully')
            this.$eventHub.$emit('jobTemplateSaved', response.data)
            this.$emit('close')
          } else {
            notifyError(this, 'Failed to Save Job Template', response.errors)
          }
        }
        this.isProcessing = false
      },
      async findCommunities(text, cb) {
        await findCommunities(this, text, cb, 'jobTemplates/edit@communityId')
      },
      setCommunityWithExistsCheck(community) {
        if (!community) {
          return
        }
        this.setCommunity(community)
        communityExistsButNotAssociated(
          this,
          community,
          this.$store.get('jobTemplates/edit@clientId'),
          this.$store.get('jobTemplates/edit@builderId')
        )
      },
      setCommunity(community) {
        this.selectedCommunity = community
        this.$store.set('jobTemplates/edit@communityId', community.id)
      },
      verifyClientOrBuilderSelected() {
        if (!this.$store.get('jobTemplates/edit@builderId') && !this.$store.get('jobTemplates/edit@clientId')) {
          const data = {
            message: 'No client or builder has been selected for this job template. Please select a client or builder to proceed.'
          }
          this.$eventHub.$emit('show-modal-5', 'No Client Or Builder', data, '500px')
          return false
        }
        return true
      },
      verifyCommunityAutocompleteSelected() {
        verifyAutocompleteSelected(this, 'jobTemplates@edit.communityId', 'community', this.selectedCommunity)
      },
      findClients(text, cb) {
        this.$store.set('jobTemplates/edit@clientId', null)
        this.$store.set('jobTemplates/edit@clientName', null)
        this.findCompany(text, Constants.COMPANY.TYPES.CLIENT)
        if (Array.isArray(this.autocompleteClients)) {
          cb(this.autocompleteClients)
        }
      },
      findBuilders(text, cb) {
        this.$store.set('jobTemplates/edit@builderId', null)
        this.$store.set('jobTemplates/edit@builderName', null)
        this.findCompany(text, Constants.COMPANY.TYPES.BUILDER)
        if (Array.isArray(this.autocompleteBuilders)) {
          cb(this.autocompleteBuilders)
        }
      },
      findCompany(text, type) {
        if (text) {
          this.$store.dispatch('companies/fetchByNameStartsWithAndType', {
            type: type,
            name: text
          })
        }
      },
      maybeLoadTaskAssignees() {
        this.$store.dispatch('jobTemplates/hydrateTaskAssignees')
      },
      async maybeLoadClient() {
        const clientId = this.$store.get('jobTemplates@edit.clientId')
        if (clientId) {
          const response = await this.$store.dispatch('fetchById', {
            endpoint: Constants.ENTITY_ENDPOINTS.COMPANIES,
            id: clientId
          })

          if (OK === response.status) {
            this.selectedClient = response.data
            this.clientContacts = contactsForSelect(response.data.contacts, this.edit.clientContactId)
            this.$store.commit('companies/autocompleteResultsClient', response.data)
          }
        }
      },
      setClient(client) {
        if (!client) {
          return
        }

        this.selectedClient.id = client.id
        this.$store.set('jobTemplates/edit@clientId', client.id)
        this.$store.set('jobTemplates/edit@clientName', client.name)

        if (client.contacts) {
          this.clientContacts = contactsForSelect(client.contacts)
        }
        if (client.contacts.length === 1) {
          this.$store.set('jobTemplates/edit@clientContactId', client.contacts[0].id)
        }
        this.$store.commit('companies/autocompleteResultsClient', [])

        if (_.get(client, 'quickBooksPreferences.onHold')) {
          this.onHoldWarning = true
          this.$validator.errors.add({
            field: 'clientId',
            msg: 'WARNING: ' + client.name + '\'s account is On Hold'
          })
        }
      },
      verifyBillableClientAutocompleteSelected() {
        verifyAutocompleteSelected(this, 'jobTemplates@edit.clientId', 'billable client', this.selectedClient)
      },
      async maybeLoadBuilder() {
        const builderId = this.$store.get('jobTemplates@edit.builderId')
        if (builderId) {
          const response = await this.$store.dispatch('fetchById', {
            endpoint: Constants.ENTITY_ENDPOINTS.COMPANIES,
            id: builderId
          })
          if (OK === response.status) {
            this.selectedBuilder = response.data
            this.builderContacts = contactsForSelect(response.data.contacts, this.edit.builderContactId)
            this.$store.commit('companies/autocompleteResultsBuilder', response.data)
          }
        }
      },
      setBuilder(builder) {
        if (!builder) {
          return
        }

        this.selectedBuilder.id = builder.id
        this.$store.set('jobTemplates/edit@builderId', builder.id)
        this.$store.set('jobTemplates/edit@builderName', builder.name)
        this.builderContacts = contactsForSelect(builder.contacts)
        if (builder.contacts.length === 1) {
          this.$store.set('jobTemplates/edit@builderContactId', builder.contacts[0].id)
        }
        this.$store.commit('companies/autocompleteResultsBuilder', [])
      },
      verifyBuilderAutocompleteSelected() {
        verifyAutocompleteSelected(this, 'jobTemplates@edit.builderId', 'builder', this.selectedBuilder)
      },
      noCommunitySelected() {
        this.selectedCommunity = { id: null, name: null}
      },
      async maybeLoadCommunity() {
        const communityId = this.$store.get('jobTemplates@edit.communityId')
        if (communityId) {
          const response = await this.$store.dispatch('fetchById', {
            endpoint: Constants.ENTITY_ENDPOINTS.COMMUNITIES,
            id: communityId
          })

          if (OK === response.status) {
            this.selectedCommunity = response.data
          }
        }
      },
      setAssignee(task, assigneeOption, index) {
        this.$store.set('jobTemplates/taskAssignee', {
          index: index,
          nameId: task.nameId,
          assigneeName: assigneeOption ? assigneeOption.label : null,
          assigneeId: assigneeOption ? assigneeOption.value : null
        })
        this.$forceUpdate()
      },
      // Due Dates are no longer being used in templates, but may be used again in the future.
      // setDueDatePlusDays(task, days) {
      //   if (days == null) {
      //     days = 0
      //   }
      //   this.$store.set('jobTemplates/taskDueDatePlusDays', {
      //     nameId: task.nameId,
      //     dueDatePlusDays: days
      //   })
      //   this.$forceUpdate()
      // },
      setActiveAssigneeAutocomplete(selectedInput) {
        this.selectedAssigneeInput = selectedInput
      }
    },
    async mounted() {
      this.$store.commit('jobTemplates/resetEdit')

      await this.$store.cache.dispatch('priorities/fetchByEntityType', 'Job')

      // creating new job template from an existing job
      if (this.data && this.data.job) {
        await this.$store.dispatch('jobTemplates/fetchTasks', this.data.job.id)
        this.$store.set('jobTemplates/edit@jobName', this.data.job.name)
        this.$store.set('jobTemplates/edit@processDefinitionKey', this.data.job.processDefinitionKey)
        this.$store.set('jobTemplates/edit@departmentId', this.data.job.departmentId)
        this.$store.set('jobTemplates/edit@priority', this.data.job.priority)

        if (this.data.job.clientId) {
          this.$store.set('jobTemplates/edit@clientId', this.data.job.clientId)
          this.$store.set('jobTemplates/edit@clientName', this.data.job.clientName)
          this.selectedClient.id = this.data.job.clientId
          this.selectedClient.name = this.data.job.clientName
          this.clientContacts = this.data.job.clientContacts
          if (this.data.job.clientContactId) {
            this.$store.set('jobTemplates/edit@clientContactId', this.data.job.clientContactId)
          }
        }
        if (this.data.job.builderId) {
          this.$store.set('jobTemplates/edit@builderId', this.data.job.builderId)
          this.$store.set('jobTemplates/edit@builderName', this.data.job.builderName)
          this.selectedBuilder.id = this.data.job.builderId
          this.selectedBuilder.name = this.data.job.builderName
          this.builderContacts = this.data.job.builderContacts
          if (this.data.job.builderContactId) {
            this.$store.set('jobTemplates/edit@builderContactId', this.data.job.builderContactId)
          }
        }

        if (_.get(this.data, 'job.community.id')) {
          this.$store.set('jobTemplates/edit@communityId', this.data.job.community.id)
          this.selectedCommunity = this.data.job.community
        }

      } else {
        const response = await this.$store.dispatch('fetchById', {
          'endpoint': Constants.ENTITY_ENDPOINTS.JOB_TEMPLATES,
          id: this.data.id
        })

        if (OK === response.status) {
          this.$store.set('jobTemplates/hydrateEdit', response.data)
          this.maybeLoadTaskAssignees()
          await Promise.all([
            this.maybeLoadClient(),
            this.maybeLoadBuilder(),
            this.maybeLoadCommunity()
          ])
        }
      }
      this.$eventHub.$on('communitySaved', this.setCommunity)
      this.$eventHub.$on('noCommunitySelected', this.noCommunitySelected)
    },
    destroyed() {
      this.$store.commit('jobTemplates/resetEdit')
      this.$eventHub.$off('communitySaved')
      this.$eventHub.$off('noCommunitySelected')
    },
    watch: {
      'selectedClient.name': {
        handler: function (client) {
          if (!client) {
            this.$store.set('jobTemplates/edit@clientId', null)
            this.$store.set('jobTemplates/edit@clientName', null)
            this.$store.set('jobTemplates/edit@clientContactId', null)
            this.clientContacts = []
            this.$store.commit('companies/autocompleteResultsClient', [])
          }
        }
      },
      'selectedBuilder.name': {
        handler: function (builder) {
          if (!builder) {
            this.$store.set('jobTemplates/edit@builderId', null)
            this.$store.set('jobTemplates/edit@builderName', null)
            this.$store.set('jobTemplates/edit@builderContactId', null)
            this.$store.commit('companies/autocompleteResultsBuilder', [])
            this.builderContacts = []
          }
        }
      },
      'selectedCommunity.name': {
        handler: function (community) {
          if (!community) {
            this.$store.set('jobTemplates/edit@communityId', null)
          }
        }
      }
    }
  }
</script>

<style scoped>
  label {
    font-weight: bold;
    display: block;
    margin-bottom: 0;
  }
  .row {
    padding: 8px 0;
  }
  .bulk-tasks {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #ebeef5;
    color: #606266;
  }
  .bulk-tasks td {
    border: 1px solid #ebeef5;
  }
  .bulk-tasks th {
    padding: 12px;
    color: #909399;
    text-transform: uppercase;
  }
  .bulk-tasks td {
    padding: 3px 12px;
  }
</style>
